import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import type { TableRequestBody, TableResponseBody } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import type { InfiniteScrollItemsResult } from 'utils/react/useInfiniteScrollItems';
import { useApi } from 'utils/request/useApi';
import type { FacetedFilterAttributeValueOption } from '../FacetedFilter/FacetedFilterAttributeSelect';
import type { FacetedFilterAttributeOption } from '../FacetedFilter/FacetedFilterNewAttributeSelect';

const DEFAULT_LIMIT = 20;

interface Props {
  attributesLimit?: number;
  valuesLimit?: number;
  /**
   * Set true to get attributes without current snapshot restiction
   */
  globalAttributes: boolean;
}

interface AttributesHook {
  loadAttributes: (
    startIndex: number,
    filter: string,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeOption>>;
  makeLoadAttributeValues: (
    attribute: string,
  ) => (
    startIndex: number,
    filter: string,
    limit?: number,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeValueOption>>;
}

export const useAttributes = (props: Props): AttributesHook => {
  const api = useApi();
  const snapshotId = useSnapshotId();

  const loadAttributes = async (startIndex: number, filter: string) => {
    const tableResponse = await api.request<
      TableResponseBody<{ keyName: string }>,
      TableRequestBody<{ keyName: string }>
    >({
      url: '/tables/attributes/settings/keys',
      method: 'POST',
      data: {
        columns: ['keyName'],
        filters: filter ? { keyName: ['like', filter] } : {},
        pagination: {
          limit: props.attributesLimit ?? DEFAULT_LIMIT,
          start: startIndex,
        },
      },
    });

    let items = tableResponse.data.map((x) => ({ value: x.keyName }));

    if (!props.globalAttributes && snapshotId !== null) {
      // When snapshot is selected, we want to gray out attributes not present on the snapshot.
      const inCurrentSnapshotResponse = await api.request<
        TableResponseBody<{ keyName: string }>,
        TableRequestBody<{ keyName: string }>
      >({
        url: '/tables/attributes/filters/keys',
        method: 'POST',
        data: {
          columns: ['keyName'],
          filters: {
            or: items.map((item) => ({ keyName: ['eq', item.value] })),
          },
          snapshot: snapshotId,
        },
      });
      const inCurrentSnapshotSet = new Set(
        inCurrentSnapshotResponse.data.map(prop('keyName')),
      );
      items = items.map((item) =>
        inCurrentSnapshotSet.has(item.value)
          ? item
          : {
              ...item,
              grayedOutReason:
                'This attribute is not available in the current snapshot.',
            },
      );
    }

    return {
      items,
      totalCount: tableResponse._meta.count,
    };
  };

  const makeLoadAttributeValues =
    (attribute: string) =>
    async (startIndex: number, filter: string, limit?: number) => {
      const tableResponse = await api.request<
        TableResponseBody<{ value: string }>,
        TableRequestBody<{ value: string }>
      >({
        url: '/tables/attributes/settings/values',
        method: 'POST',
        data: {
          columns: ['value'],
          filters: {
            keyName: ['eq', attribute],
            ...(filter ? { value: ['like', filter] } : {}),
          },
          pagination: {
            limit: limit ?? props.valuesLimit ?? DEFAULT_LIMIT,
            start: startIndex,
          },
        },
      });

      let items = tableResponse.data;

      if (!props.globalAttributes && snapshotId !== null) {
        // When snapshot is selected, we want to gray out attribute values not present on the snapshot.
        const inCurrentSnapshotResponse = await api.request<
          TableResponseBody<{ value: string }>,
          TableRequestBody<{ value: string }>
        >({
          url: '/tables/attributes/filters/values',
          method: 'POST',
          data: {
            columns: ['value'],
            filters: {
              keyName: ['eq', attribute],
              or: items.map((item) => ({ value: ['eq', item.value] })),
            },
            snapshot: snapshotId,
          },
        });
        const inCurrentSnapshotSet = new Set(
          inCurrentSnapshotResponse.data.map(prop('value')),
        );
        items = items.map((item) =>
          inCurrentSnapshotSet.has(item.value)
            ? item
            : {
                ...item,
                grayedOutReason:
                  'This attribute is not available in the current snapshot.',
              },
        );
      }

      return {
        items,
        totalCount: tableResponse._meta.count,
      };
    };

  return {
    loadAttributes,
    makeLoadAttributeValues,
  };
};
